import { useEffect, useState } from 'react';
import AppointmentModal from './AppointmentModal';
import { useAppointmentModalStore } from '../stores/appointmentModalStore';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../config/env';
import { useTreatmentCatalogConfig } from '../lib/treatmentCatalog';

export default function ConnectedAppointmentModal() {
    const { isOpen, pendingData, closeModal } = useAppointmentModalStore();
    const { getAccessTokenSilently } = useAuth0();
    const { serviceVariantsEnabled } = useTreatmentCatalogConfig();

    const [providerOptions, setProviderOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [patientOptions, setPatientOptions] = useState<
        Array<{ id: string; name: string; avatarUrl?: string | null; mrn?: string | null; phone?: string | null; dob?: string | null }>
    >([]);
    const [appointmentTypes, setAppointmentTypes] = useState<any[]>([]);
    const [locationOptions, setLocationOptions] = useState<Array<{ id: string; name: string; isPrimary?: boolean }>>([]);
    const [roomOptions, setRoomOptions] = useState<Array<{ id: string; name: string; type: string }>>([]);
    const [assetOptions, setAssetOptions] = useState<Array<{ id: string; name: string; type: string; locationId?: string | null }>>([]);
    const [defaultSlotMinutes, setDefaultSlotMinutes] = useState(30);

    useEffect(() => {
        if (isOpen) {
            loadOptions();
        }
    }, [isOpen]);

    const loadOptions = async () => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
            });

            const [patientsRes, providersRes, settingsRes, typesRes, locationsRes] = await Promise.all([
                fetch(`${API_BASE}/patients?page=1&pageSize=100`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }), // Ideally fetch all providers, but using "me" for now as in other parts
                fetch(`${API_BASE}/scheduling-settings`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`${API_BASE}/appointment-types`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`${API_BASE}/locations`, { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            const resourcesRes = await fetch(`${API_BASE}/resources`, { headers: { Authorization: `Bearer ${token}` } });

            if (patientsRes.ok) {
                const data = await patientsRes.json();
                setPatientOptions(
                    data.data.map((p: any) => ({
                        id: p.id,
                        name: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.mrn || 'Patient',
                        avatarUrl: p.avatarUrl ?? p.photoUrl ?? null,
                        mrn: p.mrn ?? null,
                        phone: p.phone ?? p.mobilePhone ?? p.contactPhone ?? null,
                        dob: p.dob ?? p.dateOfBirth ?? null,
                    }))
                );
            }

            if (providersRes.ok) {
                const me = await providersRes.json();
                // Check if there's a list of all providers endpoint? Usually distinct users with provider role.
                // For now, keep it simple: assume current user + maybe others if we had an endpoint.
                // In Appointments.tsx, it loads "me" and also derives providers from loaded appointments.
                // Let's just use "me" for now to start.
                setProviderOptions([{ id: me.id, name: `${me.firstName} ${me.lastName}` }]);
            }

            if (settingsRes.ok) {
                const scheduling = await settingsRes.json();
                setDefaultSlotMinutes(scheduling.defaultSlotMinutes ?? 30);
            }

            if (typesRes.ok) {
                const types = await typesRes.json();
                setAppointmentTypes(types);
            }

            if (locationsRes.ok) {
                const locations = await locationsRes.json();
                setLocationOptions(
                    (Array.isArray(locations) ? locations : []).map((location: any) => ({
                        id: location.id,
                        name: location.name,
                        isPrimary: Boolean(location.isPrimary),
                    }))
                );
            }

            if (resourcesRes.ok) {
                const resources = await resourcesRes.json();
                const activeResources = resources.filter((resource: any) => resource.active !== false);
                const rooms = activeResources.filter((resource: any) => /room|suite|bay|bed/i.test(resource.type ?? ''));
                const assets = activeResources.filter((resource: any) => !rooms.some((room: any) => room.id === resource.id));
                setRoomOptions(rooms.map((resource: any) => ({ ...resource, locationId: resource.locationId ?? null })));
                setAssetOptions(assets.map((resource: any) => ({ ...resource, locationId: resource.locationId ?? null })));
            }

        } catch (error) {
            console.error('Failed to load modal options', error);
        }
    };

    const handleSubmit = async (appointment: any) => {
        const token = await getAccessTokenSilently({
            authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
        });

        const method = appointment.id ? 'PUT' : 'POST';
        const url = appointment.id ? `${API_BASE}/appointments/${appointment.id}` : `${API_BASE}/appointments`;
        const startDate = new Date(appointment.start);
        const endDate = new Date(startDate.getTime() + Number(appointment.durationMinutes || 30) * 60000);

        const payload = {
            patientId: appointment.patientId,
            providerId: appointment.providerId,
            locationId: appointment.locationId,
            status: appointment.status,
            appointmentTypeId: appointment.appointmentTypeId,
            billingServiceId: serviceVariantsEnabled ? appointment.billingServiceId || undefined : undefined,
            startTime: startDate.toISOString(),
            endTime: endDate.toISOString(),
            title: appointment.title || undefined,
            isTelemed: appointment.isTelemed || undefined,
            roomResourceId: appointment.roomResourceId || undefined,
            assetResourceIds: Array.isArray(appointment.assetResourceIds) ? appointment.assetResourceIds : [],
        };

        const res = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            let message = 'Failed to save appointment';
            try {
                const body = await res.json();
                if (body && typeof body.message === 'string') message = body.message;
            } catch {}
            throw new Error(message);
        }

        closeModal();
    };

    const getToken = async () => {
        return await getAccessTokenSilently({
            authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
        });
    }

    if (!isOpen) return null;

    return (
        <AppointmentModal
            isOpen={isOpen}
            onClose={closeModal}
            onSubmit={handleSubmit}
            initialData={pendingData}
            providerOptions={providerOptions}
            patientOptions={patientOptions}
            appointmentTypes={appointmentTypes}
            locationOptions={locationOptions}
            roomOptions={roomOptions}
            assetOptions={assetOptions}
            medicalFormOptions={[]}
            serviceVariantsEnabled={serviceVariantsEnabled}
            defaultSlotMinutes={defaultSlotMinutes}
            getToken={getToken}
        />
    );
}
